import { scrollParent } from './scrollParent';
import { edgeScroll } from './edgeScroll';
import { targetAt, type Held } from './dragTargets';

export type { Held } from './dragTargets';

/**
 * One drag, from the press that might become one to the release that ends it.
 * No React in here: a pointer move at sixty frames a second across a board of
 * hundreds of tiles must not re-render anything, so the lifted node is driven
 * through CSS custom properties and the rest is reported back in whole
 * gestures — lifted, reordered, held over something, sprung elsewhere, done.
 */

/** Far enough that a click from a shaky hand is still a click. */
const THRESHOLD = 6;

/**
 * Every gesture runs off one clock, started when the cursor arrives over
 * something and reset when it leaves. Pause near a tile's edge and the others
 * shuffle to open a slot; pause over its middle and it stays put while a
 * grouping builds instead. Sweeping past does neither — the gesture is where
 * you stop, not where you passed.
 */
const HOVER_MS = 260;

/** Just under a second reads as deliberate without being a pause. */
const MERGE_MS = 900;

/** Holding still fires no pointer events, and holding still is a gesture. */
const PROBE_MS = 100;

const DRAG_VARS = ['--drag-x', '--drag-y', '--drag-w', '--drag-h'];

export type Press = {
  id: string;
  node: HTMLElement;
  x: number;
  y: number;
  /** Where inside the element it was grabbed, so it doesn't jump on lift. */
  grabX: number;
  grabY: number;
  w: number;
  h: number;
};

export type Outcome =
  /** Escape, a cancelled pointer, or a press that never became a drag. */
  | { kind: 'cancelled'; lifted: boolean }
  | { kind: 'reorder'; lifted: true; order: readonly string[] }
  | { kind: 'merge'; lifted: true; targetId: string };

export function runDrag({
  press,
  getIds,
  canMerge,
  onLift,
  onOrder,
  onHolding,
  onSpring,
  onDone,
}: {
  press: Press;
  /**
   * Read rather than passed once: a drop zone can take you somewhere else
   * mid-drag, and what's arrangeable changes when it does.
   */
  getIds: () => readonly string[];
  /** False where grouping means nothing, and holding a sortable does nothing. */
  canMerge: boolean;
  onLift: () => void;
  onOrder: (order: readonly string[]) => void;
  onHolding: (held: Held | undefined) => void;
  /**
   * A drop zone was held long enough. Fired mid-drag, not on release: the
   * point of holding over one is to be taken there while still carrying the
   * thing, so you can put it down where you actually want it.
   */
  onSpring: (targetId: string) => void;
  onDone: (outcome: Outcome) => void;
}): () => void {
  const { id, grabX, grabY, w, h } = press;
  const startX = press.x;
  const startY = press.y;
  const scroller = scrollParent(press.node);

  /** Reassigned when the tile is re-rendered somewhere else. See `lifted`. */
  let node = press.node;
  let live: readonly string[] = getIds();
  let dragging = false;
  let done = false;
  let lastProbe = 0;
  let frame: number | undefined;
  let x = startX;
  let y = startY;

  /** What the cursor has been sitting on, and since when. */
  let dwellOn: string | undefined;
  let dwellFrom = 0;
  /** Set once the dwell is long enough to mean it. */
  let holding: Held | undefined;

  /**
   * The element being carried. Springing into another container re-renders
   * the tile somewhere new, so the node held at the start is detached and a
   * fresh one has taken its place — same id, different element. React puts
   * the lifted class on it; this puts the numbers back.
   */
  function lifted(): HTMLElement | undefined {
    if (node.isConnected) return node;

    const found = document.querySelector<HTMLElement>(`[data-sort-id="${CSS.escape(id)}"]`);
    if (found === null) return undefined;

    node = found;
    node.style.setProperty('--drag-w', `${w}px`);
    node.style.setProperty('--drag-h', `${h}px`);
    return node;
  }

  function place() {
    const element = lifted();
    if (element === undefined) return;

    element.style.setProperty('--drag-x', `${x - grabX}px`);
    element.style.setProperty('--drag-y', `${y - grabY}px`);
  }

  function lift() {
    dragging = true;
    node.style.setProperty('--drag-w', `${w}px`);
    node.style.setProperty('--drag-h', `${h}px`);
    place();
    onLift();
    frame = requestAnimationFrame(tick);
  }

  function stopHolding() {
    if (holding === undefined) return;
    holding = undefined;
    onHolding(undefined);
  }

  /**
   * Whatever the cursor rests on takes the cursor's place — or takes the
   * dragged thing inside it, if the rest was over its middle and long enough
   * to mean it.
   *
   * The lifted node can't be hit; it has no pointer events. So this finds
   * either a neighbour or the hole it left, and the hole isn't sortable, so
   * resting there does nothing. That's what stops it oscillating.
   */
  function swap() {
    // A spring lands the drag somewhere else entirely, where the old
    // arrangement means nothing. Same members in a different order is a
    // reorder in progress and has to be left alone; a different cast is a
    // different container.
    const ids = getIds();
    if (!sameMembers(ids, live)) live = ids;

    const over = targetAt(x, y, id, live);

    if (over === undefined) {
      dwellOn = undefined;
      stopHolding();
      return;
    }

    if (over.id !== dwellOn) {
      dwellOn = over.id;
      dwellFrom = performance.now();
      stopHolding();
      return;
    }

    const held = performance.now() - dwellFrom;

    if (over.middle && (over.kind === 'drop' || canMerge)) {
      if (holding === undefined && held >= MERGE_MS) {
        holding = { id: over.id, kind: over.kind };
        onHolding(holding);

        // A drop zone acts now rather than waiting for a release: it takes
        // you into that container still carrying this, so the release can be
        // spent on where it goes rather than on getting there.
        if (over.kind === 'drop') {
          stopHolding();
          dwellOn = undefined;
          onSpring(over.id);
        }
      }
      // Never reorder out from under a hold that is about this target.
      return;
    }

    stopHolding();
    if (held < HOVER_MS) return;

    const next = [...live];
    const from = next.indexOf(id);
    const to = next.indexOf(over.id);
    if (from === -1 || to === -1 || from === to) return;

    next.splice(from, 1);
    next.splice(to, 0, id);
    live = next;
    // The hole is under the cursor now, so the clock restarts wherever it
    // goes next rather than immediately firing again.
    dwellOn = undefined;
    onOrder(next);
  }

  function tick() {
    frame = requestAnimationFrame(tick);
    edgeScroll(scroller, y);

    if (performance.now() - lastProbe > PROBE_MS) {
      lastProbe = performance.now();
      swap();
    }
  }

  function onMove(event: PointerEvent) {
    x = event.clientX;
    y = event.clientY;

    if (!dragging) {
      if (Math.hypot(x - startX, y - startY) < THRESHOLD) return;
      lift();
      return;
    }

    place();
    swap();
  }

  function finish(keep: boolean) {
    if (done) return;
    done = true;
    stop();

    if (!dragging) {
      onDone({ kind: 'cancelled', lifted: false });
      return;
    }

    clearVars();

    // Released on something it was held over: that's a grouping, and the
    // shuffling it did on the way there was never the point.
    if (keep && holding !== undefined) {
      onDone({ kind: 'merge', lifted: true, targetId: holding.id });
    } else if (keep) onDone({ kind: 'reorder', lifted: true, order: live });
    else onDone({ kind: 'cancelled', lifted: true });
  }

  function onUp() {
    finish(true);
  }
  function onCancel() {
    finish(false);
  }
  function onKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    finish(false);
  }

  function clearVars() {
    for (const name of DRAG_VARS) lifted()?.style.removeProperty(name);
  }

  function stop() {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onCancel);
    window.removeEventListener('keydown', onKeyDown);
    if (frame !== undefined) cancelAnimationFrame(frame);
  }

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
  window.addEventListener('keydown', onKeyDown);

  return () => {
    // Unmounted mid-drag: drop the listeners, keep the arrangement.
    done = true;
    stop();
    clearVars();
  };
}

/** Same cast, whatever the running order. */
function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const inB = new Set(b);
  return a.every((id) => inB.has(id));
}
